import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AgencyPublicationModel, AgencyPublicationRequestOptions } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agencyPublication/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agencyPublication/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agencyPublication/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agencyPublication/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agencyPublication/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agencyPublication/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/agencyPublication/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agencyPublication/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agencyPublication/DELETE_FAILURE'

export type AgencyPublicationModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
}

// Initial State
const initialState: AgencyPublicationModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencyPublications(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencyPublications.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agency_publications.json', options)
      .then(({ data }: { data: AgencyPublicationModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PUBLICATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgencyPublication(agencyPublication: DeleteParams<AgencyPublicationModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency_publications/${agencyPublication.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PUBLICATION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkDeleteAgencyPublications(
  params: { agency_id: number, agency_publication_ids: number[] },
  options: AgencyPublicationRequestOptions = {},
) {
  const {
    agency_id, agency_publication_ids,
  } = params

  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      agency_id,
      agency_publication_ids,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api('/agency_publications/bulk_delete.json', options, config)
      .then(({ data }: { data: Partial<AgencyPublicationModel>[] }) => {
        data.forEach((agencyPublication) => {
          const normalizedJson = normalize(agencyPublication, Schemas.AGENCY_PUBLICATION)
          dispatch(deleteEntity(normalizedJson))
        })

        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkCreateAgencyPublications(
  params: { agencyId: number, publicationIds: number[] },
  options?: AgencyPublicationRequestOptions,
) {
  const { agencyId, publicationIds } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_id: agencyId,
      publication_ids: publicationIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_publications/bulk_create.json', options, config)
      .then(({ data }: { data: AgencyPublicationModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PUBLICATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyPublicationModuleState = initialState,
  action: Action = { type: '' },
): AgencyPublicationModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
